// Conversation state: messages, streaming, retries, the lead offer and the
// unread counter. Persisted per tab so it survives moving between pages.

import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { ApiError, streamChat, track } from "./lib/api.ts";
import { API_KEY, requestContext, toParent } from "./lib/bridge.ts";
import { leadTrigger } from "./lib/lead-trigger.ts";
import { newSessionId, sessionStore, type LeadPrefill, type LeadState, type Saved } from "./lib/session.ts";
import type { Card, Message, Rating, WidgetConfig } from "./lib/types.ts";

const DEFAULT_LEAD_AFTER = 3;

const id = () => crypto.randomUUID();

export function useChat(config: WidgetConfig | null, isOpen: boolean) {
  const store = useMemo(() => sessionStore(API_KEY), []);
  const [state, setState] = useState<Saved>(() => store.load());
  const [streaming, setStreaming] = useState(false);
  const [unread, setUnread] = useState(0);
  const abort = useRef<AbortController | null>(null);
  const openRef = useRef(isOpen);
  openRef.current = isOpen;

  useEffect(() => store.save(state), [state, store]);

  useEffect(() => {
    if (isOpen) setUnread(0);
  }, [isOpen]);
  useEffect(() => toParent({ type: "unread", count: unread }), [unread]);

  const patch = useCallback((messageId: string, change: Partial<Message> | ((m: Message) => Partial<Message>)) => {
    setState((s) => ({
      ...s,
      messages: s.messages.map((m) => (m.id === messageId ? { ...m, ...(typeof change === "function" ? change(m) : change) } : m)),
    }));
  }, []);

  const send = useCallback(
    async (text: string, ref: string | null = null) => {
      const message = text.trim();
      if (!message || streaming || !config) return;

      const userId = id();
      const answerId = id();
      setState((s) => ({
        ...s,
        messages: [...s.messages, { id: userId, role: "user", content: message }, { id: answerId, role: "assistant", content: "", status: "streaming" }],
        // The thank-you card has done its job once the conversation moves on.
        lead: s.lead === "sent" ? "dismissed" : s.lead,
      }));
      setStreaming(true);
      const controller = new AbortController();
      abort.current = controller;

      const page = await requestContext();
      const pageRef = ref ?? page?.ref ?? null;
      const context = page || pageRef ? { ...(page ? { url: page.url } : {}), ...(pageRef ? { ref: pageRef } : {}) } : undefined;
      const sessionId = state.sessionId;
      let conversationId = state.conversationId;
      track("message_sent", sessionId, { length: message.length }, conversationId);

      // Chunks arrive faster than the screen paints: batch them per frame.
      let pending = "";
      let frame = 0;
      const flush = () => {
        frame = 0;
        const delta = pending;
        pending = "";
        if (delta) patch(answerId, (m) => ({ content: m.content + delta }));
      };

      try {
        for await (const event of streamChat({ message, session_id: sessionId, ...(context ? { context } : {}) }, controller.signal)) {
          if (event.type === "start") {
            conversationId = event.conversation_id;
            setState((s) => ({ ...s, conversationId: event.conversation_id }));
            if (event.message_id) patch(answerId, { serverId: event.message_id });
          } else if (event.type === "chunk") {
            pending += event.content;
            if (!frame) frame = requestAnimationFrame(flush);
          } else if (event.type === "sources") {
            const cards: Card[] = [...(event.properties ?? []), ...(event.products ?? [])];
            flush();
            patch(answerId, { cards });
          } else if (event.type === "suggestions") {
            patch(answerId, { followUps: event.items });
          } else if (event.type === "retract") {
            cancelAnimationFrame(frame);
            frame = 0;
            pending = "";
            patch(answerId, { content: event.content, cards: undefined });
          } else if (event.type === "error") {
            flush();
            patch(answerId, (m) => (m.content ? { status: undefined, error: event.message } : { status: "failed", error: event.message }));
          }
        }
        cancelAnimationFrame(frame);
        flush();
        patch(answerId, (m) => (m.status === "streaming" ? { status: undefined } : {}));
        track("message_received", sessionId, {}, conversationId);
        if (!openRef.current) setUnread((n) => n + 1);
        offerLead(message);
      } catch (error) {
        cancelAnimationFrame(frame);
        flush();
        if ((error as Error).name === "AbortError") {
          patch(answerId, (m) => ({ status: undefined, content: m.content || "_Zatrzymano odpowiedź._" }));
        } else {
          const text = error instanceof ApiError ? error.message : "Coś poszło nie tak. Spróbuj ponownie.";
          const status = error instanceof ApiError ? error.status : 0;
          const retryable = status === 0 || status === 429 || status >= 500;
          // No answer at all: drop the empty bubble and mark the question as not delivered.
          setState((s) => ({
            ...s,
            messages: s.messages
              .filter((m) => !(m.id === answerId && !m.content))
              .map((m) => (m.id === userId ? { ...m, status: "failed", error: text, retryable } : m.id === answerId ? { ...m, status: undefined } : m)),
          }));
          track("error", sessionId, { status: error instanceof ApiError ? error.status : 0 });
        }
      } finally {
        abort.current = null;
        setStreaming(false);
      }
    },
    [config, streaming, state.sessionId, state.conversationId, patch],
  );

  /**
   * After an answer: the form when the question asks for it (lib/lead-trigger.ts),
   * else the offer after a few questions. A form already sent stays sent; an
   * explicit wish for contact comes back even after "Nie teraz".
   */
  function offerLead(question: string) {
    if (!config?.features.leadCapture) return;
    const after = Number(config.features.leadCaptureAfterMessages ?? DEFAULT_LEAD_AFTER);
    const trigger = leadTrigger(question);
    setState((s) => {
      if (s.lead === "sent" || s.lead === "open") return s;
      if (trigger && (trigger.show === "open" || s.lead === "hidden")) {
        const prefill: LeadPrefill = {
          message: question.slice(0, 500),
          ...(trigger.phone ? { phone: trigger.phone } : {}),
          ...(trigger.email ? { email: trigger.email } : {}),
        };
        track("lead_form_shown", s.sessionId, { trigger: trigger.reason }, s.conversationId);
        return { ...s, lead: trigger.show === "open" ? "open" : "offered", leadPrefill: prefill };
      }
      const asked = s.messages.filter((m) => m.role === "user" && m.status !== "failed").length;
      if (s.lead !== "hidden" || asked < after) return s;
      track("lead_form_shown", s.sessionId, { trigger: "auto" }, s.conversationId);
      return { ...s, lead: "offered" };
    });
  }

  /** A button asked for it ("Umów prezentację"): the form, with the request as its message. */
  const openLead = useCallback(
    (prefill: LeadPrefill, reason: string) => {
      if (!config?.features.leadCapture) return false;
      setState((s) => {
        if (s.lead === "sent") return s;
        track("lead_form_shown", s.sessionId, { trigger: reason }, s.conversationId);
        return { ...s, lead: "open", leadPrefill: prefill };
      });
      return true;
    },
    [config],
  );

  /** A card opened: the visitor is interested, so the offer shows if nothing has yet. */
  const noticeInterest = useCallback(() => {
    if (!config?.features.leadCapture) return;
    setState((s) => {
      if (s.lead !== "hidden") return s;
      track("lead_form_shown", s.sessionId, { trigger: "card" }, s.conversationId);
      return { ...s, lead: "offered" };
    });
  }, [config]);

  const retry = useCallback(
    (messageId: string) => {
      const failed = state.messages.find((m) => m.id === messageId);
      if (!failed) return;
      setState((s) => ({ ...s, messages: s.messages.filter((m) => m.id !== messageId) }));
      void send(failed.content);
    },
    [state.messages, send],
  );

  /**
   * Thumbs under an answer: the latest choice counts (the results page takes
   * the last event). An answer that did not help is the moment to offer a person.
   */
  const rate = useCallback(
    (messageId: string, rating: Rating) => {
      setState((s) => {
        const answer = s.messages.find((m) => m.id === messageId);
        if (!answer?.serverId || answer.rating === rating) return s;
        track("answer_rated", s.sessionId, { message_id: answer.serverId, rating }, s.conversationId);
        const messages = s.messages.map((m) => (m.id === messageId ? { ...m, rating } : m));
        if (rating === "down" && config?.features.leadCapture && s.lead === "hidden") {
          track("lead_form_shown", s.sessionId, { trigger: "rated-down" }, s.conversationId);
          return { ...s, messages, lead: "offered" };
        }
        return { ...s, messages };
      });
    },
    [config],
  );

  const stop = useCallback(() => abort.current?.abort(), []);

  const reset = useCallback(() => {
    abort.current?.abort();
    setState({ sessionId: newSessionId(), conversationId: null, messages: [], lead: "hidden" });
  }, []);

  const setLead = useCallback((lead: LeadState) => setState((s) => ({ ...s, lead })), []);

  return {
    messages: state.messages,
    sessionId: state.sessionId,
    conversationId: state.conversationId,
    lead: state.lead,
    leadPrefill: state.leadPrefill,
    streaming,
    send,
    retry,
    stop,
    reset,
    setLead,
    openLead,
    noticeInterest,
    rate,
    consentGiven: store.consentGiven,
    giveConsent: store.giveConsent,
  };
}

export type Chat = ReturnType<typeof useChat>;
